/**
 * HLOS-28 — OPFS path conventions para o vault.
 *
 * Espelha o padrão de paths definido em
 * `docs/architecture/IDENTITY_AND_KEY_MANAGEMENT.md` §4.2:
 *
 *   /hlos/vault/
 *   ├── <user_id>/
 *   │   ├── vault.json     (criptografado: DEK wrapped × 2 KEKs)
 *   │   ├── meta.json      (público: KDF params, created_at, version)
 *   │   └── consent.json   (opt-in granular CL-08, criptografado separado)
 *
 * Constraints Constitucionais:
 *   - CL-07 (Modelo = ativo): zero chamada de rede para carregar/salvar
 *   - CL-08 (Ética fechada): vault.json NUNCA é plaintext em disco
 *   - CL-09 (Explicabilidade): paths auditáveis, versionados em git
 *
 * Em produção, esses paths mapeiam para OPFS (Origin Private File System).
 * Em testes, são strings puras (sem I/O) — o `VaultStore` injeta
 * a abstração de I/O.
 */

import { VAULT_VERSION } from "./constants";

// ─────────────────────────────────────────────────────────────────────────────
// Path conventions
// ─────────────────────────────────────────────────────────────────────────────

/** Diretório raiz do vault em OPFS. */
export const VAULT_ROOT_DIR = "hlos/vault";

/** Path do vault criptografado (JSON, contém DEK wrapped × 2 KEKs). */
export function vaultFilePath(userId: string): string {
  return `${VAULT_ROOT_DIR}/${userId}/vault.json`;
}

/** Path de metadados públicos (KDF params, vault_id, created_at). */
export function metaFilePath(userId: string): string {
  return `${VAULT_ROOT_DIR}/${userId}/meta.json`;
}

/** Path do log de consent (CL-08: opt-in granular, criptografado separado). */
export function consentFilePath(userId: string): string {
  return `${VAULT_ROOT_DIR}/${userId}/consent.enc`;
}

/** Versão atual do schema do vault (espelha `vault.VAULT_VERSION`). */
export function currentVaultVersion(): number {
  return VAULT_VERSION;
}

/**
 * Valida `userId` antes de construir paths (CL-08: zero parametrização
 * permissiva em paths).
 *
 * @throws InvalidVaultPathError se `userId` contém separadores ou
 *         caracteres de controle (path traversal bloqueado).
 */
export class InvalidVaultPathError extends Error {
  constructor(userId: string, reason: string) {
    super(`Invalid vault path for userId="${userId}": ${reason}`);
    this.name = "InvalidVaultPathError";
  }
}

export function validateUserId(userId: string): void {
  if (typeof userId !== "string" || userId.length === 0) {
    throw new InvalidVaultPathError(String(userId), "must be non-empty string");
  }
  if (userId.length > 256) {
    throw new InvalidVaultPathError(userId, "must be ≤ 256 chars");
  }
  // Bloqueia path traversal e chars de controle (CL-08).
  if (/[/\\.\x00-\x1f]/.test(userId)) {
    throw new InvalidVaultPathError(
      userId,
      "must not contain '/', '\\\\', '.', or control chars",
    );
  }
}
